import { useCallback, useEffect, useRef, useState } from "react";

import { Colors, Icon } from "@blueprintjs/core";
import capitalize from "lodash/capitalize";

import { Box, Flex } from "@/components/layout/flexbox";
import { Flag } from "@/components/pages_logged_in/roleplays/details/scenario/edit/Flag";
import { Mp3Player } from "@/components/pieces/audio/Mp3Player";
import { BRAND_PURPLE, LIGHT_BRAND_PURPLE } from "@/css/constants";
import { RoleplayVoicesQuery, useRoleplayVoicesQuery } from "@/graphql";
import { NonNullableArrayItem } from "@/types/utils";
import { ensureNonNullArray } from "@/utils/array";

type Voice = NonNullableArrayItem<
  NonNullable<RoleplayVoicesQuery["roleplayVoices"]>
>;

const ageLabels = {
  YOUNG: "Young",
  MIDDLE_AGE: "Middle-aged",
  OLD: "Elderly",
};

interface VoiceOptionProps {
  voice: Voice;
  isSelected: boolean;
  onSelect: (voice: Voice) => void;
}

function VoiceOption({ voice, isSelected, onSelect }: VoiceOptionProps) {
  const handleSelect = useCallback(() => {
    onSelect(voice);
  }, [onSelect, voice]);
  return (
    <Flex
      alignItems="center"
      justifyContent="space-between"
      css={{
        "&:hover": {
          bg: "rgba(0,0,0,0.05)",
        },
      }}
      px={12}
      backgroundColor={isSelected ? LIGHT_BRAND_PURPLE : Colors.WHITE}
    >
      <Flex
        flex={1}
        alignItems="center"
        css={{
          borderBottom: `1px solid ${
            isSelected ? LIGHT_BRAND_PURPLE : "#E2E2E2"
          }`,
        }}
      >
        <div className="flex items-center gap-4 w-full">
          <Flag countryCode={voice.countryCode} />
          <div className="flex items-center gap-2 whitespace-nowrap text-sm w-40">
            <div className="w-12">{capitalize(voice.name)}</div>
            <div className="text-gray-500">
              {voice.age && `• ${ageLabels[voice.age]}`}
            </div>
          </div>
          <Box flex={1}>
            <Mp3Player
              minimal
              backgroundColor="transparent"
              src={voice.sampleUrl ?? undefined}
              playIcon={<Icon icon="play" size={20} />}
              pauseIcon={<Icon icon="pause" size={20} />}
              showProgressIndicator={false}
            />
          </Box>
        </div>
        <Box
          onClick={handleSelect}
          fontWeight={700}
          fontSize={14}
          color={isSelected ? Colors.BLACK : BRAND_PURPLE}
          css={{
            cursor: "pointer",
          }}
          py={12}
          pl={12}
        >
          {isSelected ? "Selected" : "Select"}
        </Box>
      </Flex>
    </Flex>
  );
}

interface SelectedVoiceProps {
  voice: Voice;
}

function SelectedVoice({ voice }: SelectedVoiceProps) {
  return (
    <div className="flex items-center gap-4 w-full">
      <Flag countryCode={voice.countryCode} />
      <div className="whitespace-nowrap text-base">
        {capitalize(voice.name)}{" "}
        <span className="text-gray-500">
          {voice.age && `• ${ageLabels[voice.age]}`}
        </span>
      </div>
      <div className="flex flex-1">
        <Mp3Player
          minimal
          backgroundColor="transparent"
          src={voice.sampleUrl ?? undefined}
          playIcon={<Icon icon="play" size={20} />}
          pauseIcon={<Icon icon="pause" size={20} />}
          showProgressIndicator={false}
        />
      </div>
    </div>
  );
}

interface VoiceSelectorProps {
  voiceId: string;
  onChangeVoiceId: (voiceId: string) => void;
}

export function VoiceSelector({
  voiceId,
  onChangeVoiceId,
}: VoiceSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState<Voice | null>(null);
  const selectorRef = useRef<HTMLDivElement>(null);

  const { data } = useRoleplayVoicesQuery({
    onCompleted: () => {},
    onError: () => {},
  });

  const voices = ensureNonNullArray(data?.roleplayVoices);

  useEffect(() => {
    if (voiceId && voices.length) {
      const voice = voices.find((v) => v.voiceId === voiceId);
      setSelectedVoice(voice || null);
    }
  }, [voiceId, voices]);

  const handleSelectVoice = useCallback(
    (voice: Voice) => {
      setSelectedVoice(voice);
      onChangeVoiceId(voice.voiceId);
    },
    [setSelectedVoice, onChangeVoiceId],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectorRef.current &&
        !selectorRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
      }}
      ref={selectorRef}
    >
      <Flex
        className={`bas`}
        onClick={() => setIsOpen(!isOpen)}
        css={{
          borderRadius: isOpen ? "5px 5px 0px 0px" : "5px",
          cursor: "pointer",
          gap: 24,
        }}
        width={"100%"}
        py={12}
        px={16}
        alignItems={"center"}
      >
        {selectedVoice ? (
          <SelectedVoice voice={selectedVoice} />
        ) : (
          <Flex flex={1} alignItems="center">
            Select a voice...
          </Flex>
        )}
        <Icon icon={isOpen ? "chevron-up" : "chevron-down"} size={19} />
      </Flex>
      <Box
        className={`bas`}
        css={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          overflowY: "auto",
          zIndex: 1000,
          display: isOpen ? "block" : "none",
          borderRadius: "0px 0px 8px 8px",
          borderTop: "0px",
          transition: "all 0.3s ease",
        }}
        maxHeight={225}
        backgroundColor={Colors.WHITE}
        py={0}
      >
        {voices.map((voice) => (
          <VoiceOption
            key={voice.voiceId}
            voice={voice}
            isSelected={selectedVoice?.voiceId === voice.voiceId}
            onSelect={handleSelectVoice}
          />
        ))}
      </Box>
    </Box>
  );
}
